import { Button, Layout, Menu, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import React from 'react';

import { BrandLogo } from '@/atoms/BrandLogo';
import { APP_VERSION } from '@/constants/version';
import { AssetLibraryIcon, Icon } from '@/icons';

/** A single navigation item in the sidebar; set `divider: true` to insert a horizontal rule. */
export interface SidebarNavItem {
  key: string;
  label?: string;
  path?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** When true, inserts a visible horizontal rule between navigation groups (same style as primary ↔ workspace). */
  divider?: boolean;
}

/** Props for the `SidebarNav` collapsible left navigation panel. */
export interface SidebarNavProps {
  topItems: SidebarNavItem[];
  workspaceItems: SidebarNavItem[];
  activeKey: string;
  onNavigate: (path: string) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

const { Sider } = Layout;

/** Converts a `SidebarNavItem` into an Ant Design `MenuProps['items']` entry (or a divider). */
const toMenuItem = (
  item: SidebarNavItem,
  collapsed: boolean,
  onNavigate: (path: string) => void,
): NonNullable<MenuProps['items']>[number] => {
  if (item.divider) {
    return { type: 'divider' };
  }
  const iconNode = item.icon ?? <AssetLibraryIcon />;
  const path = item.path;
  const label = item.label ?? '';
  return {
    key: item.key,
    icon: iconNode,
    label,
    title: label,
    className: collapsed ? 'app-shell__menu-item--collapsed' : undefined,
    style: collapsed
      ? {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingLeft: 0,
          paddingRight: 0,
        }
      : undefined,
    disabled: item.disabled,
    onClick: path ? () => onNavigate(path) : undefined,
  };
};

/** Split items at the first `divider: true` row (divider itself is dropped). Used for a visible rule between groups. */
function partitionAtDivider(items: SidebarNavItem[]): {
  before: SidebarNavItem[];
  after: SidebarNavItem[];
} | null {
  const dividerIndex = items.findIndex((item) => item.divider);
  if (dividerIndex === -1) {
    return null;
  }
  return {
    before: items.slice(0, dividerIndex),
    after: items.slice(dividerIndex + 1),
  };
}

/** Props for the `BrandSection` sub-component at the top of the sidebar. */
interface BrandSectionProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

/** Renders the constant brand logo and collapse toggle. */
function BrandSection({
  collapsed,
  onToggleCollapsed,
}: Readonly<BrandSectionProps>): React.ReactElement {
  return (
    <div className="app-shell__brand">
      <div className="app-shell__brand-row">
        <div className="app-shell__brand-client">
          {collapsed ? (
            <button
              type="button"
              className="app-shell__brand-logo-trigger"
              aria-label="Expand navigation"
              onClick={onToggleCollapsed}
            >
              <BrandLogo
                variant="mark"
                colorMode="dark"
                className="app-shell__brand-logo app-shell__brand-logo--mark"
              />
            </button>
          ) : (
            <BrandLogo
              variant="full"
              colorMode="dark"
              className="app-shell__brand-logo app-shell__brand-logo--full"
            />
          )}
        </div>
        {collapsed ? null : (
          <Tooltip title="Collapse navigation">
            <Button
              type="text"
              shape="circle"
              size="small"
              icon={<Icon type="vertical-right" />}
              className="app-shell__collapse-btn"
              aria-label="Collapse navigation"
              onClick={onToggleCollapsed}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
}

/** Props for the `WorkspaceMenuSection` sub-component that renders the scoped workspace nav group. */
interface WorkspaceMenuSectionProps {
  activeKey: string;
  collapsed: boolean;
  onNavigate: (path: string) => void;
  workspaceItems: SidebarNavItem[];
}

/** Renders the workspace-scoped navigation section, splitting items at a `divider` row when present. */
function WorkspaceMenuSection({
  activeKey,
  collapsed,
  onNavigate,
  workspaceItems,
}: Readonly<WorkspaceMenuSectionProps>): React.ReactElement | null {
  const workspacePartition = partitionAtDivider(workspaceItems);
  const workspaceMenuItems: NonNullable<MenuProps['items']> = workspacePartition
    ? []
    : workspaceItems.map((item) => toMenuItem(item, collapsed, onNavigate));
  const hasWorkspaceSection =
    workspacePartition === null
      ? workspaceMenuItems.length > 0
      : workspacePartition.before.length > 0 || workspacePartition.after.length > 0;

  if (!hasWorkspaceSection) {
    return null;
  }

  const workspaceMenuProps = {
    mode: 'inline' as const,
    theme: 'dark' as const,
    inlineCollapsed: collapsed,
    selectedKeys: [activeKey],
    className: 'app-shell__menu app-shell__menu--workspace',
  };

  if (!workspacePartition) {
    return (
      <>
        <div className="app-shell__menu-divider" aria-hidden="true" />
        <Menu {...workspaceMenuProps} items={workspaceMenuItems} />
      </>
    );
  }

  return (
    <>
      <div className="app-shell__menu-divider" aria-hidden="true" />
      {workspacePartition.before.length > 0 ? (
        <Menu
          {...workspaceMenuProps}
          items={workspacePartition.before.map((item) => toMenuItem(item, collapsed, onNavigate))}
        />
      ) : null}
      <div
        className="app-shell__menu-divider app-shell__menu-divider--workspace-split"
        aria-hidden="true"
      />
      {workspacePartition.after.length > 0 ? (
        <Menu
          {...workspaceMenuProps}
          items={workspacePartition.after.map((item) => toMenuItem(item, collapsed, onNavigate))}
        />
      ) : null}
    </>
  );
}

/** Collapsible left sidebar composed of a brand section, top-level nav menu, and workspace-scoped nav section. */
export const SidebarNav: React.FC<SidebarNavProps> = ({
  topItems,
  workspaceItems,
  activeKey,
  onNavigate,
  collapsed,
  onToggleCollapsed,
}) => {
  const topPartition = partitionAtDivider(topItems);
  const topMenuProps = {
    mode: 'inline' as const,
    theme: 'dark' as const,
    inlineCollapsed: collapsed,
    selectedKeys: [activeKey],
    className: 'app-shell__menu app-shell__menu--top',
  };

  return (
    <Sider
      width={260}
      collapsedWidth={72}
      collapsible
      collapsed={collapsed}
      trigger={null}
      className="app-shell__sider"
    >
      <BrandSection collapsed={collapsed} onToggleCollapsed={onToggleCollapsed} />

      <div className="app-shell__menu-stack">
        {topPartition ? (
          <>
            {topPartition.before.length > 0 ? (
              <Menu
                {...topMenuProps}
                items={topPartition.before.map((item) => toMenuItem(item, collapsed, onNavigate))}
              />
            ) : null}
            <div className="app-shell__menu-divider" aria-hidden="true" />
            {topPartition.after.length > 0 ? (
              <Menu
                {...topMenuProps}
                items={topPartition.after.map((item) => toMenuItem(item, collapsed, onNavigate))}
              />
            ) : null}
          </>
        ) : (
          <Menu
            {...topMenuProps}
            items={topItems.map((item) => toMenuItem(item, collapsed, onNavigate))}
          />
        )}
        <WorkspaceMenuSection
          activeKey={activeKey}
          collapsed={collapsed}
          onNavigate={onNavigate}
          workspaceItems={workspaceItems}
        />
      </div>

      <div
        className={`app-shell__version-badge${collapsed ? ' app-shell__version-badge--collapsed' : ''}`}
        aria-label={`App version ${APP_VERSION}`}
      >
        {collapsed ? (
          <Tooltip title={`v${APP_VERSION}`} placement="right">
            <span className="app-shell__version-badge-mark">v</span>
          </Tooltip>
        ) : (
          <span className="app-shell__version-badge-label">v{APP_VERSION}</span>
        )}
      </div>
    </Sider>
  );
};

export default SidebarNav;
